import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Image as ImageIcon, Upload } from "lucide-react";
import { API } from "@/lib/api";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const emptyHouse = {
  name: "", address: "", image: "", status: "rehab", cycle: 1, retained: false,
  purchase_price: 0, rehab_cost: 0, sale_price: 0, rehab_progress: 0, notes: "",
};
const STATUS_OPTS = [
  { v: "rehab", l: "In Rehab" },
  { v: "for_sale", l: "For Sale" },
  { v: "sold", l: "Sold" },
  { v: "rental", l: "Retained Rental" },
];

export default function Admin() {
  const [houses, setHouses] = useState([]);
  const [members, setMembers] = useState([]);
  const [returns, setReturns] = useState([]);
  const [form, setForm] = useState(emptyHouse);
  const [ret, setRet] = useState({ year: new Date().getFullYear(), rate_percent: "" });
  const [photoHouse, setPhotoHouse] = useState(null);
  const [photoCaption, setPhotoCaption] = useState("");
  const [updateNote, setUpdateNote] = useState("");

  const load = () => {
    api.get("/houses").then((r) => setHouses(r.data));
    api.get("/admin/members").then((r) => setMembers(r.data));
    api.get("/returns").then((r) => setReturns(r.data));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addHouse = async (e) => {
    e.preventDefault();
    try {
      await api.post("/houses", {
        ...form,
        cycle: Number(form.cycle),
        purchase_price: Number(form.purchase_price),
        rehab_cost: Number(form.rehab_cost),
        sale_price: Number(form.sale_price),
        rehab_progress: Number(form.rehab_progress),
      });
      toast.success("Property added");
      setForm(emptyHouse);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateHouse = async (id, updates) => {
    try {
      await api.put(`/houses/${id}`, updates);
      toast.success("Property updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteHouse = async (id) => {
    await api.delete(`/houses/${id}`);
    toast.success("Property removed");
    if (photoHouse === id) setPhotoHouse(null);
    load();
  };

  const uploadPhoto = async (houseId, file, label) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/houses/${houseId}/photos?label=${label}&caption=${encodeURIComponent(photoCaption)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      setPhotoCaption("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
  };

  const deletePhoto = async (houseId, photoId) => {
    await api.delete(`/houses/${houseId}/photos/${photoId}`);
    toast.success("Photo removed");
    load();
  };

  const addUpdate = async (houseId) => {
    if (!updateNote.trim()) return;
    try {
      await api.post(`/houses/${houseId}/updates`, { note: updateNote });
      toast.success("Update posted");
      setUpdateNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to post update");
    }
  };

  const deleteUpdate = async (houseId, updateId) => {
    await api.delete(`/houses/${houseId}/updates/${updateId}`);
    toast.success("Update removed");
    load();
  };

  const declareReturn = async (e) => {
    e.preventDefault();
    try {
      const payload = { year: Number(ret.year) };
      if (ret.rate_percent !== "") payload.rate_percent = Number(ret.rate_percent);
      const { data } = await api.post("/admin/returns", payload);
      toast.success(`Distributed ${fmt(data.total_payout)} (${data.rate_percent}% per share)`);
      setRet({ ...ret, rate_percent: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#0A2E26] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#C49A45]" />
          </div>
          <div>
            <h1 className="font-display font-bold text-3xl text-[#0A2E26] tracking-tight">Fund Admin</h1>
            <p className="text-[#5C5C5C]">Manage properties, returns and members.</p>
          </div>
        </div>

        <Tabs defaultValue="properties" className="mt-8">
          <TabsList data-testid="admin-tabs" className="bg-white border border-[#E5E5E5]">
            <TabsTrigger value="properties" data-testid="tab-properties">Properties</TabsTrigger>
            <TabsTrigger value="returns" data-testid="tab-returns">Annual Returns</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
          </TabsList>

          {/* PROPERTIES */}
          <TabsContent value="properties" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <form onSubmit={addHouse} data-testid="add-house-form"
                className="bg-white border border-[#E5E5E5] rounded-xl p-6 space-y-3">
                <h2 className="font-display font-bold text-lg text-[#0A2E26] flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Add property
                </h2>
                <div><Label>Name</Label><Input data-testid="house-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1" /></div>
                <div><Label>Address</Label><Input data-testid="house-address" value={form.address} onChange={(e) => set("address", e.target.value)} required className="mt-1" /></div>
                <div><Label>Image URL</Label><Input data-testid="house-image" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="https://…" className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger data-testid="house-status" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Cycle #</Label><Input data-testid="house-cycle" type="number" min={1} value={form.cycle} onChange={(e) => set("cycle", e.target.value)} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Purchase $</Label><Input data-testid="house-purchase" type="number" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} className="mt-1" /></div>
                  <div><Label>Rehab $</Label><Input data-testid="house-rehab" type="number" value={form.rehab_cost} onChange={(e) => set("rehab_cost", e.target.value)} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Sale $</Label><Input data-testid="house-sale" type="number" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} className="mt-1" /></div>
                  <div><Label>Rehab %</Label><Input data-testid="house-progress" type="number" min={0} max={100} value={form.rehab_progress} onChange={(e) => set("rehab_progress", e.target.value)} className="mt-1" /></div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <Label>Retained rental (11th)</Label>
                  <Switch data-testid="house-retained" checked={form.retained} onCheckedChange={(v) => set("retained", v)} />
                </div>
                <Button type="submit" data-testid="submit-house" className="w-full bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md">
                  Add property
                </Button>
              </form>

              <div className="lg:col-span-2 space-y-3">
                {houses.length === 0 && <p className="text-[#5C5C5C]">No properties yet.</p>}
                {houses.map((h) => {
                  const profit = (h.sale_price || 0) - (h.purchase_price || 0) - (h.rehab_cost || 0);
                  const photos = h.photos || [];
                  const open = photoHouse === h.id;
                  return (
                    <div key={h.id} data-testid={`admin-house-${h.id}`}
                      className="bg-white border border-[#E5E5E5] rounded-xl p-5">
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex-1 min-w-[180px]">
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-[#0A2E26]">{h.name}</h3>
                            <StatusBadge status={h.status} />
                          </div>
                          <p className="text-sm text-[#5C5C5C]">{h.address} · Cycle #{h.cycle}</p>
                          <p className="text-sm text-[#5C5C5C] mt-1">
                            Cost {fmt((h.purchase_price || 0) + (h.rehab_cost || 0))}
                            {h.status === "sold" && <> · Sold {fmt(h.sale_price)} · <span className="text-[#059669] font-semibold">Profit {fmt(profit)}</span></>}
                            {h.distributed && <span className="ml-1 text-xs text-[#2563EB]">(distributed)</span>}
                          </p>
                        </div>
                        <Button variant="outline" size="sm" data-testid={`photos-toggle-${h.id}`}
                          onClick={() => setPhotoHouse(open ? null : h.id)}
                          className="gap-2 border-[#0A2E26]/20 text-[#0A2E26]">
                          <ImageIcon className="w-4 h-4" /> Photos ({photos.length})
                        </Button>
                        <Select value={h.status} onValueChange={(v) => updateHouse(h.id, { status: v })}>
                          <SelectTrigger data-testid={`update-status-${h.id}`} className="w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" data-testid={`delete-house-${h.id}`}
                          onClick={() => deleteHouse(h.id)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {open && (
                        <div className="mt-4 pt-4 border-t border-[#E5E5E5]" data-testid={`photo-manager-${h.id}`}>
                          <Input
                            data-testid={`photo-caption-${h.id}`}
                            value={photoCaption}
                            onChange={(e) => setPhotoCaption(e.target.value)}
                            placeholder="Optional caption for the next photo (e.g. 'Kitchen demo complete')"
                            className="mb-3 bg-white"
                          />
                          <div className="flex flex-wrap gap-2">
                            {["before", "after", "progress"].map((lbl) => (
                              <label key={lbl}
                                className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A2E26] bg-[#F9F8F6] border border-[#E5E5E5] rounded-md px-3 py-2 hover:bg-white transition-colors capitalize">
                                <Upload className="w-3.5 h-3.5" /> Add {lbl}
                                <input type="file" accept="image/*" className="hidden"
                                  data-testid={`upload-${lbl}-${h.id}`}
                                  onChange={(e) => { uploadPhoto(h.id, e.target.files[0], lbl); e.target.value = ""; }} />
                              </label>
                            ))}
                          </div>
                          {photos.length > 0 && (
                            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {photos.map((p) => (
                                <div key={p.id} className="relative group rounded-lg overflow-hidden border border-[#E5E5E5]">
                                  <img src={`${API}/files/${p.storage_path}`} alt={p.label}
                                    className="w-full h-24 object-cover" />
                                  <span className="absolute top-1 left-1 text-[10px] font-semibold text-white bg-black/50 px-1.5 py-0.5 rounded capitalize">{p.label}</span>
                                  <button data-testid={`delete-photo-${p.id}`}
                                    onClick={() => deletePhoto(h.id, p.id)}
                                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                  {p.caption && (
                                    <span className="absolute bottom-0 inset-x-0 text-[9px] text-white bg-black/55 px-1.5 py-0.5 truncate">{p.caption}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* CMT project updates */}
                          <div className="mt-5 pt-4 border-t border-[#E5E5E5]" data-testid={`updates-manager-${h.id}`}>
                            <p className="text-xs font-semibold uppercase tracking-wide text-[#5C5C5C] mb-2">CMT project updates</p>
                            <div className="flex gap-2">
                              <Input
                                data-testid={`update-note-${h.id}`}
                                value={updateNote}
                                onChange={(e) => setUpdateNote(e.target.value)}
                                placeholder="Post a short update (e.g. 'Roof replaced, drywall next week')"
                                className="bg-white"
                              />
                              <Button data-testid={`post-update-${h.id}`} onClick={() => addUpdate(h.id)}
                                className="bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md shrink-0">
                                Post
                              </Button>
                            </div>
                            {(h.updates || []).length > 0 && (
                              <div className="mt-3 space-y-2">
                                {(h.updates || []).map((u) => (
                                  <div key={u.id} className="flex items-start justify-between gap-3 bg-[#F9F8F6] border border-[#E5E5E5] rounded-md px-3 py-2">
                                    <div>
                                      <p className="text-sm text-[#1A1A1A]">{u.note}</p>
                                      <p className="text-xs text-[#5C5C5C] mt-0.5">{new Date(u.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <button data-testid={`delete-update-${u.id}`} onClick={() => deleteUpdate(h.id, u.id)}
                                      className="text-red-600 hover:bg-red-50 rounded p-1 shrink-0">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* RETURNS */}
          <TabsContent value="returns" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <form onSubmit={declareReturn} data-testid="declare-return-form"
                className="bg-white border border-[#E5E5E5] rounded-xl p-6 space-y-4">
                <h2 className="font-display font-bold text-lg text-[#0A2E26]">Declare annual return</h2>
                <div>
                  <Label>Year</Label>
                  <Input data-testid="return-year" type="number" value={ret.year} onChange={(e) => setRet({ ...ret, year: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Override rate % (optional)</Label>
                  <Input data-testid="return-rate" type="number" step="0.1" placeholder="Leave blank to auto-calc from sold houses"
                    value={ret.rate_percent} onChange={(e) => setRet({ ...ret, rate_percent: e.target.value })} className="mt-1" />
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    Blank = auto-calculated from realized profit of sold, undistributed homes. A value applies that % of each $100 share.
                  </p>
                </div>
                <Button type="submit" data-testid="submit-return" className="w-full bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md">
                  Distribute to all members
                </Button>
              </form>

              <div className="lg:col-span-2 bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E5E5]"><h2 className="font-display font-bold text-lg text-[#0A2E26]">Past distributions</h2></div>
                {returns.length === 0 ? (
                  <p className="px-6 py-10 text-center text-[#5C5C5C]">No returns declared yet.</p>
                ) : (
                  <table className="w-full text-sm" data-testid="returns-table">
                    <thead><tr className="text-left text-[#5C5C5C] border-b border-[#E5E5E5]">
                      <th className="px-6 py-3 font-medium">Period</th>
                      <th className="px-6 py-3 font-medium">Rate</th>
                      <th className="px-6 py-3 font-medium">Per share</th>
                      <th className="px-6 py-3 font-medium text-right">Total payout</th>
                    </tr></thead>
                    <tbody>
                      {returns.map((r) => (
                        <tr key={r.id} className="border-b border-[#E5E5E5] last:border-0">
                          <td className="px-6 py-3 font-medium text-[#0A2E26]">{r.period || `${r.quarter || ""} ${r.year}`}</td>
                          <td className="px-6 py-3 text-[#5C5C5C]">{r.rate_percent}%</td>
                          <td className="px-6 py-3 text-[#5C5C5C]">${r.per_share}</td>
                          <td className="px-6 py-3 text-right font-semibold text-[#0A2E26]">{fmt(r.total_payout)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>

          {/* MEMBERS */}
          <TabsContent value="members" className="mt-6">
            <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E5E5E5]">
                <h2 className="font-display font-bold text-lg text-[#0A2E26]">Members ({members.length})</h2>
              </div>
              {members.length === 0 ? (
                <p className="px-6 py-10 text-center text-[#5C5C5C]">No members yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="members-table">
                  <thead><tr className="text-left text-[#5C5C5C] border-b border-[#E5E5E5]">
                    <th className="px-6 py-3 font-medium">Name</th>
                    <th className="px-6 py-3 font-medium">Email</th>
                    <th className="px-6 py-3 font-medium text-right">Shares</th>
                    <th className="px-6 py-3 font-medium text-right">Balance</th>
                    <th className="px-6 py-3 font-medium">Reinvest</th>
                  </tr></thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.id} className="border-b border-[#E5E5E5] last:border-0">
                        <td className="px-6 py-3 font-medium text-[#0A2E26]">{m.name}</td>
                        <td className="px-6 py-3 text-[#5C5C5C]">{m.email}</td>
                        <td className="px-6 py-3 text-right text-[#0A2E26]">{Number(m.shares).toFixed(2)}</td>
                        <td className="px-6 py-3 text-right text-[#0A2E26]">{fmt(m.balance)}</td>
                        <td className="px-6 py-3">{m.reinvest ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
